import { isFastly } from "@faire/web--source/common/images/optimizeImages";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { TelemetryManager } from "@faire/web--source/common/opentelemetry/TelemetryManager";
import { IImage } from "@faire/web-api--source/indigofair/data/IImage";
import { Span } from "@opentelemetry/api";

import { getAltTag } from "@faire/retailer/features/Marketplace/utils";
import { PRODUCT_TILE_TYPE_MAP } from "@faire/retailer/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/components/ProductImage/__internal__/common";
import { BrandPageImageProps } from "@faire/retailer/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/components/ProductImage/brand/brandProductImagePropsILeanProductTileAdapter";
import { ProductTileImageProps } from "@faire/retailer/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/components/ProductImage/ProductTileImageProps";
import { ProductTileType } from "@faire/retailer/features/MarketplaceItems/ComposableProductTile/ProductTileType";
import { getValidImageOrPlaceholder } from "@faire/retailer/features/MarketplaceItems/ProductDetailsTile/getValidImageOrPlaceholder";
import { useValidateImage } from "@faire/retailer/features/MarketplaceItems/ProductDetailsTile/useValidateImage";
import { useBrandProductResultData } from "@faire/retailer/views/brand/hooks/useBrandProductResultData";

const useClientHooks = (image: IImage) => {
  const { strictLocalize } = useStrictLocalization();

  const productsQueryData = useBrandProductResultData();
  const { showVerticalImagery } = productsQueryData;

  const validImageHolder = useValidateImage(image);
  const validImage =
    image?.url && !isFastly(image.url)
      ? getValidImageOrPlaceholder(validImageHolder)
      : image;

  return { strictLocalize, showVerticalImagery, validImage };
};

export const useBrandProductTileImageProps = ({
  productToken,
  brandName,
  productName,
  image,
}: BrandPageImageProps): ProductTileImageProps => {
  const productTileSpan = TelemetryManager.get().getManagedSpan(
    TelemetryManager.get().startManagedSpan(
      "ProductTile",
      {
        productToken: productToken,
      },
      undefined
    ) ?? ""
  );

  const imageLoadSpan: Span | undefined = TelemetryManager.get().startSpan(
    "ProductTileImage",
    undefined,
    productTileSpan ?? undefined
  );

  const { showVerticalImagery, strictLocalize, validImage } =
    useClientHooks(image);

  return {
    image: validImage,
    showVerticalImagery,
    imageAltText: getAltTag(
      {
        productName,
        pageType: PRODUCT_TILE_TYPE_MAP[ProductTileType.BRAND],
        brandName,
      },
      strictLocalize
    ),
    shouldUseLazyImageLoading: false,
    //Client prop
    onLoad: () => TelemetryManager.get().endSpan(imageLoadSpan),
  };
};
